import React, { useEffect, useState } from "react";
import IGroupSubsectionUI from "@models/IGroupSubsectionUI";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwButton from "@components/shared/OwButton/OwButton";
import { MultiProductGroupSection } from "./MultiProductGroupSection/MultiProductGroupSection";
import { v4 } from "uuid";
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
    DragStartEvent,
    DragOverlay,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable";

type Props = {
    catalogGuid: string;
    initialSections: IGroupSubsectionUI[];
    updateSectionsFn: (sections: IGroupSubsectionUI[]) => void;
};

export const MultiProductGroupDetail = (props: Props) => {
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const [sections, setSections] = useState<IGroupSubsectionUI[]>(props.initialSections);
    const [openAll, setOpenAll] = useState(false);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [activeSection, setActiveSection] = useState<IGroupSubsectionUI | null>(null);

    // Sync incoming prop changes (e.g. on initial load from parent state)
    useEffect(() => {
        setSections(props.initialSections);
    }, [props.initialSections]);

    // Propagate every change up to CreateCatalogItemSidebar
    useEffect(() => {
        props.updateSectionsFn(sections);
    }, [sections]);

    const addSection = () => {
        const newSection: IGroupSubsectionUI = {
            uuid: v4(),
            title: "",
            sequence: 0,
            items: [],
        };
        const newSections = [newSection, ...sections].map((s, index) => ({ ...s, sequence: index }));
        setSections(newSections);
    };

    // updateSection: splice session-only sections with deleteFlag, otherwise map
    const updateSection = (section: IGroupSubsectionUI) => {
        if (section.deleteFlag && !section.id) {
            setSections(sections.filter((s) => s.uuid !== section.uuid));
            return;
        }
        setSections(sections.map((s) => (s.uuid === section.uuid ? section : s)));
    };

    const handleDragStart = (event: DragStartEvent) => {
        const { active } = event;
        setActiveId(active.id as string);
        const section = sections.find((s) => s.uuid === active.id);
        if (section) setActiveSection(section);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            setSections((current) => {
                const oldIndex = current.findIndex((s) => s.uuid === active.id);
                const newIndex = current.findIndex((s) => s.uuid === over.id);
                return arrayMove(current, oldIndex, newIndex).map((s, i) => ({ ...s, sequence: i }));
            });
        }

        setActiveId(null);
        setActiveSection(null);
    };

    const activeSections = sections.filter((s) => !s.deleteFlag);
    const hasValidationError = activeSections.length === 0;

    return (
        <div className={"MultiProductGroupDetail"}>
            <div className={"d-flex align-items-center mb-2"}>
                <div className={"me-auto d-flex align-items-center"}>
                    <OwLabel title={"subsections"} isBold={true} className={"me-2"} noBottomMargin />
                    <OwButton title={"add"} className={"btn-warning btn-sm"} leftIconClass={"fas fa-plus"} fn={addSection} />
                </div>
                <OwButton
                    title={openAll ? "close_all" : "open_all"}
                    className={"btn-primary btn-sm no-wrap"}
                    leftIconClass={"fas fa-eye"}
                    fn={() => setOpenAll(!openAll)}
                />
            </div>

            {hasValidationError && (
                <div className={"alert alert-warning py-1 mb-2 small"}>
                    <OwLabel title={"multi_group_requires_at_least_one_subsection"} noBottomMargin />
                </div>
            )}

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                <SortableContext items={sections.map((s) => s.uuid)} strategy={verticalListSortingStrategy}>
                    {sections.map((section) => (
                        <div key={section.uuid} className={"mt-2"}>
                            <MultiProductGroupSection
                                section={section}
                                catalogGuid={props.catalogGuid}
                                updateSectionFn={updateSection}
                                id={section.uuid}
                                openOverride={openAll}
                                isActive={activeId === section.uuid}
                            />
                        </div>
                    ))}
                </SortableContext>

                <DragOverlay adjustScale={true}>
                    {activeSection && (
                        <div className="drag-overlay">
                            <MultiProductGroupSection
                                section={activeSection}
                                catalogGuid={props.catalogGuid}
                                updateSectionFn={() => {}}
                                id={activeSection.uuid}
                                isDragOverlay={true}
                            />
                        </div>
                    )}
                </DragOverlay>
            </DndContext>
        </div>
    );
};
